import { useEffect, useState } from "react";
import { Lock, Fingerprint } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { bioRecord } from "../lib/bio";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const FAILS = "hb-unlock-fails";
const LOCK_UNTIL = "hb-unlock-wait-until";

export default function LockScreen() {
  const { unlock, unlockWithBio } = useStore();
  const [bio, setBio] = useState(false);
  useEffect(() => { bioRecord().then((r) => setBio(!!r)).catch(() => {}); }, []);
  const bioUnlock = async () => {
    setErr("");
    setBusy(true);
    try {
      await unlockWithBio();
    } catch (e) {
      setErr(e?.name === "NotAllowedError" ? "Face ID / Touch ID was cancelled. Try again or use your passcode." : "Couldn't unlock with Face ID / Touch ID. Use your passcode.");
    }
    setBusy(false);
  };
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(() => Math.max(0, Math.ceil((Number(localStorage.getItem(LOCK_UNTIL) || 0) - Date.now()) / 1000)));
  useEffect(() => {
    if (!wait) return;
    const t = setTimeout(() => setWait((w) => Math.max(0, w - 1)), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  const submit = async (e) => {
    e.preventDefault();
    if (wait) return;
    setBusy(true);
    setErr("");
    try {
      await unlock(pass);
      localStorage.removeItem(FAILS);
      localStorage.removeItem(LOCK_UNTIL);
    } catch {
      // After 3 wrong tries, wait 2s, 4s, 8s… (max 5 min) before the next attempt
      const fails = Number(localStorage.getItem(FAILS) || 0) + 1;
      localStorage.setItem(FAILS, String(fails));
      const secs = fails >= 3 ? Math.min(300, 2 ** (fails - 2)) : 0;
      if (secs) {
        localStorage.setItem(LOCK_UNTIL, String(Date.now() + secs * 1000));
        setWait(secs);
      }
      setErr(secs ? `That passcode didn't work. Try again in ${secs} seconds.` : "That passcode didn't work. Try again.");
    }
    setBusy(false);
  };
  return (
    <div className="min-h-screen grid place-items-center px-4 bg-background">
      <form onSubmit={submit} className="w-full max-w-sm text-center fade-up" data-testid="lock-screen">
        <div className="h-14 w-14 rounded-2xl bg-primary text-primary-foreground grid place-items-center mx-auto"><Lock className="h-6 w-6" /></div>
        <h1 className="text-3xl font-extrabold mt-5">Welcome back</h1>
        <p className="text-muted-foreground mt-1">Your budget is encrypted. {bio ? "Use Face ID / Touch ID or your passcode to unlock it." : "Enter your passcode to unlock it."}</p>
        {bio && (
          <>
            <Button type="button" className="w-full mt-6 h-12 text-base" onClick={bioUnlock} disabled={busy} data-testid="unlock-bio-button"><Fingerprint className="h-5 w-5 mr-2" /> Unlock with Face ID / Touch ID</Button>
            <p className="text-xs text-muted-foreground mt-4">or use your passcode</p>
          </>
        )}
        <Input type="password" autoFocus={!bio} value={pass} onChange={(e) => setPass(e.target.value)} className={bio ? "mt-2 h-12 text-lg text-center" : "mt-6 h-12 text-lg text-center"} placeholder="Passcode" data-testid="unlock-passcode-input" />
        {err && <p className="text-expense text-sm mt-2" data-testid="unlock-error">{err}</p>}
        <Button type="submit" variant={bio ? "outline" : "default"} className="w-full mt-4 h-12 text-base" disabled={busy || !pass || wait > 0} data-testid="unlock-submit-button">{busy ? "Unlocking…" : wait ? `Wait ${wait}s` : "Unlock"}</Button>
        <p className="text-xs text-muted-foreground mt-6">Forgot it? Data cannot be recovered without the passcode. You can restore from a backup after clearing site data.</p>
      </form>
    </div>
  );
}
